import {NavLink} from 'react-router-dom';
import {I18n} from 'aws-amplify/utils';

import {productCopy} from '../../../config/productCopy';
import {
    Alert,
    Anchor,
    Badge,
    Code,
    Group,
    Stack,
    Table,
    Text,
} from '@mantine/core';

import config from '../config';
import {Certificate} from '../types';

// FNP-1193 — WHAT THE PROVIDER WANTS PUBLISHED, AND WHETHER FLEXI PUBLISHED IT.
//
// ⚠️ THIS PANEL IS THE ANSWER TO "WHY IS THIS CERTIFICATE NOT ISSUED YET".
//
// Flexi writes the validation record itself through the owning zone's adapter,
// so a tenant should never copy it by hand — that hand-copying is precisely the
// step this module exists to remove. But when validation is STUCK, the record
// is the first thing anyone wants to compare against what is actually in the
// zone, and making them go and find it elsewhere is the friction the ticket is
// about.
//
// ⚠️ THE TWO NON-PROGRESSING STATES GET DIFFERENT PANELS, because they have
// different fixes. This component branches on `validationRecordWrittenAt`
// being null rather than on the status string, because that field IS the
// distinction — the status is computed from it.
export const ValidationRecordsPanel = (
    {
        certificate,
    }: {
        certificate: Certificate,
    },
) => {

    // ⚠️ FNP-1483 — NULL MEANS FLEXI HOLDS NO CONFIRMED WRITE, NOTHING MORE.
    // The provider may not have produced the record yet, or the write may have
    // landed without being stamped; the API retries it on every read. This
    // panel used to say the write did not happen, which it cannot know.
    const notConfirmed = !certificate.validationRecordWrittenAt;

    const records = certificate.validationRecords ?? [];

    // Issued certificates have no outstanding validation records, and saying
    // nothing is the right amount to say about a certificate that works.
    if (certificate.status === 'ISSUED' && records.length === 0) {
        return null;
    }

    return (
        <Stack gap={'sm'}>

            {/* ⚠️ THE `VALIDATION_NOT_WRITTEN` EXPLANATION, in two cases the
                view can tell apart. Neither claims the write failed and neither
                asks for a hand-publish: a record published by hand over a live
                one can break renewal. */}
            {certificate.status === 'VALIDATION_NOT_WRITTEN' && notConfirmed && records.length === 0 && (
                <Alert
                    color={'blue'}
                    variant={'light'}
                    title={I18n.get('Waiting for the provider')}
                >
                    <Text size={'sm'}>
                        {I18n.get('The provider has not produced the record yet.')}
                    </Text>
                </Alert>
            )}
            {certificate.status === 'VALIDATION_NOT_WRITTEN' && notConfirmed && records.length > 0 && (
                <Alert
                    color={'orange'}
                    variant={'light'}
                    title={I18n.get('The validation record is not confirmed')}
                >
                    <Stack gap={'xs'}>
                        <Text size={'sm'}>
                            {productCopy('No confirmed write yet. {product} retries on every load.')}
                        </Text>
                        <Text size={'sm'}>
                            {I18n.get('If this persists, check the connection can write records to this zone.')}
                        </Text>
                    </Stack>
                </Alert>
            )}

            {/* ⚠️ THE `STUCK` EXPLANATION. The record WAS written, so the
                question is whether it is still there and still correct — which
                is why this one links into the zone and the other does not. */}
            {!notConfirmed && certificate.status === 'STUCK' && (
                <Alert
                    color={'orange'}
                    variant={'light'}
                    title={I18n.get('Validation is not progressing')}
                >
                    <Stack gap={'xs'}>
                        <Text size={'sm'}>
                            {/* ⚠️ BOTH HALVES EARN THEIR PLACE: what happened
                                (written, not confirmed) is what separates this
                                from the never-written case above, and the
                                comparison is the only action available. */}
                            {I18n.get('Written but not confirmed. Compare it against the zone.')}
                        </Text>
                        <Text size={'xs'} c={'dimmed'}>
                            {I18n.get('Written at')}: <Code>{certificate.validationRecordWrittenAt}</Code>
                            {' · '}
                            {I18n.get('Stuck after')} {certificate.stuckAfterMinutes} {I18n.get('minutes')}
                        </Text>
                        {/* Straight to the records in the zone that should hold
                            it, rather than telling an operator to go and find
                            the zone themselves. */}
                        {certificate.validationZoneId && (
                            <Anchor
                                component={NavLink}
                                size={'sm'}
                                to={config.zoneRecordsPath(
                                    certificate.validationZoneId,
                                    certificate.connectionId,
                                )}
                            >
                                {I18n.get('Open the records in that zone')}
                            </Anchor>
                        )}
                    </Stack>
                </Alert>
            )}

            {records.length > 0 && (
                <Stack gap={4}>
                    <Text size={'sm'} fw={500}>
                        {I18n.get('Validation records')}
                    </Text>
                    <Text size={'xs'} c={'dimmed'}>
                        {/* ⚠️ A CERTIFICATE COVERING SEVERAL NAMES HAS ONE
                            RECORD PER NAME, AND THEY SETTLE INDEPENDENTLY. A
                            reader seeing three rows where one says "SUCCESS"
                            and two do not should understand that as normal
                            partial progress rather than a contradiction. */}
                        {/* ⚠️ "CONFIRMED INDEPENDENTLY" IS THE WHOLE POINT OF
                            THIS LINE. A reader seeing three rows where one
                            says SUCCESS and two do not should read that as
                            normal partial progress, not a contradiction.
                            Dropped: "so some may settle before others", which
                            restates the same fact a second time. */}
                        {I18n.get('One record per name, confirmed independently.')}
                    </Text>
                    <Table.ScrollContainer minWidth={600}>
                        <Table striped={true} withTableBorder={true} fz={'xs'}>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{I18n.get('For name')}</Table.Th>
                                    <Table.Th>{I18n.get('Record')}</Table.Th>
                                    <Table.Th>{I18n.get('Value')}</Table.Th>
                                    <Table.Th>{I18n.get('Provider status')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {records.map((record, index) => (
                                    <Table.Tr key={`${record.name}#${record.domainName}#${index}`}>
                                        <Table.Td>{record.domainName}</Table.Td>
                                        <Table.Td>
                                            <Group gap={4} wrap={'nowrap'}>
                                                <Badge variant={'light'} size={'sm'}>
                                                    {record.type}
                                                </Badge>
                                                <Code>{record.name}</Code>
                                            </Group>
                                        </Table.Td>
                                        <Table.Td>
                                            <Code style={{wordBreak: 'break-all'}}>
                                                {record.value}
                                            </Code>
                                        </Table.Td>
                                        <Table.Td>
                                            {/* The provider's own word for this
                                                name, VERBATIM and unmapped —
                                                it is not Flexi's vocabulary and
                                                coercing it into
                                                CertificateStatus would invent a
                                                claim the provider did not make. */}
                                            <Text size={'xs'} c={record.status ? undefined : 'dimmed'}>
                                                {record.status ?? I18n.get('not reported')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                </Stack>
            )}

        </Stack>
    );
};
